import classNames from "classnames";
import { DateTime } from "luxon";
import { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { common_utils } from "../../../../common/common_utils";
import { RouterInferenceHelpers } from "../../../../src/api/trpc/rootRouter";
import { AvailabilityResponse } from "../../../../src/api/trpc/time-finder";
import { DashboardUtils, RandomAvatar } from "../../dashboard/dashboard_utils";
import { LoadingThrobber } from "../../dashboard/forms/improved_components/base_components/loading_throbber";
import { SeshPrimaryButton } from "../../dashboard/forms/improved_components/base_components/sesh_primary_button";
import { SeshTextInput } from "../../dashboard/forms/improved_components/base_components/sesh_text_input";
import { WithTooltipPortaled } from "../../dashboard/forms/improved_components/tooltips/with_tooltip_portaled";
import { trpc } from "../../dashboard/utils/trpc";
import { EligibleRolesIndicator } from "./eligible_roles_indicator";
import { InputSection } from "./input-section";
import { TIME_FINDER_SETTINGS } from "./time_finder";

function setCookie(name: string, value: string, days: number) {
	const date = new Date();
	date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
	const expires = "; expires=" + date.toUTCString();
	document.cookie = name + "=" + value + expires + "; path=/; SameSite=Lax";
}

function getCookie(name: string): string | null {
	const nameEQ = name + "=";
	const ca = document.cookie.split(";");
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) === " ") c = c.substring(1, c.length);
		if (c.indexOf(nameEQ) === 0)
			return c.substring(nameEQ.length, c.length);
	}
	return null;
}

function JustCreatedModalContent(props: {
	shareableUrl: string;
	onClose: () => void;
}) {
	const [showCopied, setShowCopied] = useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(props.shareableUrl);
		setShowCopied(true);
		setTimeout(() => {
			setShowCopied(false);
		}, 2000);
	};

	const handleClose = () => {
		props.onClose();
	};

	return (
		<div className="p-4">
			<p className="mb-4">
				Your Time Finder poll has been created! Share this link with
				others:
			</p>
			<div className="mb-4 flex items-center gap-2">
				<input
					type="text"
					readOnly
					value={props.shareableUrl}
					className="flex-1 rounded-md border border-base-light bg-base-dark p-2"
					onClick={(e) => e.currentTarget.select()}
				/>
				<div className="relative">
					<button
						className="rounded-md bg-sesh-primary p-2 text-black"
						onClick={handleCopy}
						title="Copy to clipboard"
					>
						<i className="fas fa-copy"></i>
					</button>
					{showCopied && (
						<div className="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 rounded bg-sesh-primary px-2 py-1 text-xs font-bold text-black">
							Copied
							<div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-sesh-primary"></div>
						</div>
					)}
				</div>
			</div>
			<div className="mt-4 flex justify-end">
				<SeshPrimaryButton onClick={handleClose}>
					Got it
				</SeshPrimaryButton>
			</div>
		</div>
	);
}

export function PublicAvailabilityPoll() {
	const { timeFinderID: time_finder_id } = useParams<{
		timeFinderID: string;
	}>();

	const [isJustCreatedModalActive, setIsJustCreatedModalActive] =
		useState(false);
	const [shareableUrl, setShareableUrl] = useState("");
	const [userId, setUserId] = useState<string | null>(
		getCookie("time_finder_user_id")
	);

	const time_finder_query = trpc.time_finder.getByIdPublic.useQuery(
		{
			id: time_finder_id,
			user_id: userId || undefined,
		},
		{
			onSuccess: (data) => {
				setNameInputValue(
					data.availabilities.find((val) => val.id === userId)
						?.username ?? ""
				);

				// Check if there's a just_created query parameter and show the modal if it exists
				const searchParams = new URLSearchParams(
					window.location.search
				);
				if (searchParams.get("just_created") === "true") {
					const url = common_utils.getTimeFinderURL({
						server_id: data.guild_id || "",
						type: "view",
						instance_id: time_finder_id,
						is_public: true,
					});
					setShareableUrl(window.location.origin + url);
					setIsJustCreatedModalActive(true);
				}
			},
		}
	);
	const [isNameModalActive, setIsNameModalActive] = useState(false);
	const [nameInputValue, setNameInputValue] = useState("");
	const [pendingAvailabilities, setPendingAvailabilities] = useState<{
		availabilities: { id: string; availability: AvailabilityResponse }[];
	} | null>(null);

	const history = useHistory();

	const mutation = trpc.time_finder.updateAvailabilityPublic.useMutation({
		onSuccess: (data) => {
			// Store the user ID in a cookie if it was returned
			if (data.userId) {
				// Set cookie that expires in 1 year
				setCookie("time_finder_user_id", data.userId, 365);
			}
			history.go(0);
		},
	});

	const handleSubmitFromPoll = (data: {
		availabilities: { id: string; availability: AvailabilityResponse }[];
	}) => {
		// Otherwise handle it internally
		// Store the availabilities temporarily and show the name modal
		setPendingAvailabilities(data);
		setIsNameModalActive(true);
	};

	const handleNameSubmit = () => {
		if (!nameInputValue.trim() || !pendingAvailabilities) return;

		// Trim and ensure name doesn't exceed 32 characters
		const trimmedName = nameInputValue.trim().substring(0, 32);

		// Submit the stored availabilities along with the name
		mutation.mutate({
			id: time_finder_id,
			username: trimmedName,
			availabilities: pendingAvailabilities.availabilities,
			existingUserId: userId || undefined,
		});

		// Close the modal
		setIsNameModalActive(false);
	};

	// Name input modal content
	const nameModalContent = (
		<div className="p-4">
			<SeshTextInput
				label="Your Name"
				input_props={{
					type: "text",
					value: nameInputValue,
					onChange: (e) => setNameInputValue(e.currentTarget.value),
					placeholder: "Enter your name",
					autoFocus: true,
					className: "w-full",
					maxLength: 32,
				}}
			/>
			<div className="mt-4 flex justify-end">
				<SeshPrimaryButton
					is_loading={mutation.isLoading}
					onClick={handleNameSubmit}
				>
					Submit
				</SeshPrimaryButton>
			</div>
		</div>
	);

	// Just created modal content
	const justCreatedModalContent = (
		<JustCreatedModalContent
			shareableUrl={shareableUrl}
			onClose={() => setIsJustCreatedModalActive(false)}
		/>
	);

	if (!time_finder_query.isSuccess) {
		return <LoadingThrobber />;
	}

	return (
		<>
			<AvailabilityPollContent
				is_editable={false}
				time_finder_instance={time_finder_query.data}
				onSubmit={handleSubmitFromPoll}
				is_loading={mutation.isLoading || mutation.isSuccess}
				create_new_poll_link={"/time-finder"}
				user_id={userId || undefined}
				is_on_dashboard={false}
			/>
			<DashboardUtils.SeshModal
				is_active={isNameModalActive}
				title="Enter Your Name"
				content={nameModalContent}
				onClose={() => setIsNameModalActive(false)}
			/>
			<DashboardUtils.SeshModal
				is_active={isJustCreatedModalActive}
				title="Poll Created"
				content={justCreatedModalContent}
				onClose={() => {
					const url = new URL(window.location.href);
					url.searchParams.delete("just_created");
					window.history.replaceState(
						{},
						document.title,
						url.toString()
					);

					setIsJustCreatedModalActive(false);
				}}
			/>
		</>
	);
}

export function AvailabilityPollContent(props: {
	user_id?: string;
	UserRenderer?: (props: { user_id: string }) => React.ReactElement;
	is_editable: boolean;
	time_finder_instance: RouterInferenceHelpers["time_finder"]["getByIdPublic"]["output"];
	onSubmit: (data: {
		availabilities: { id: string; availability: AvailabilityResponse }[];
	}) => void;
	create_new_poll_link: string;
	is_loading: boolean;
	onEdit?: () => void;
	onDelete?: () => void;
	is_deleting?: boolean;
	is_on_dashboard: boolean;
}) {
	const history = useHistory();
	const [isJustCreatedModalActive, setIsJustCreatedModalActive] =
		useState(false);
	const [shareableUrl, setShareableUrl] = useState("");
	const [showCopied, setShowCopied] = useState(false);

	// Check for just_created parameter on mount
	useEffect(() => {
		const searchParams = new URLSearchParams(window.location.search);
		if (
			searchParams.get("just_created") === "true" &&
			props.time_finder_instance?.id
		) {
			const url = common_utils.getTimeFinderURL({
				server_id: props.time_finder_instance.guild_id || "",
				type: "view",
				instance_id: props.time_finder_instance.id,
				is_public: props.time_finder_instance.is_public,
			});

			setShareableUrl(window.location.origin + url);
			setIsJustCreatedModalActive(true);
		} else if (
			props.time_finder_instance?.is_public &&
			props.time_finder_instance?.id
		) {
			// Set the shareable URL for the public poll info section
			const url = common_utils.getTimeFinderURL({
				server_id: props.time_finder_instance.guild_id || "",
				type: "view",
				instance_id: props.time_finder_instance.id,
				is_public: true,
			});
			setShareableUrl(window.location.origin + url);
		}
	}, [props.time_finder_instance]);

	const handleCopyPublicLink = () => {
		navigator.clipboard.writeText(shareableUrl);
		setShowCopied(true);
		setTimeout(() => {
			setShowCopied(false);
		}, 2000);
	};

	// State for editing mode
	const [isEditMode, setIsEditMode] = useState(false);

	// State to control if the user's input row is visible
	const [isUserRowVisible, setIsUserRowVisible] = useState(true);

	// State for new responses
	const [newResponses, setNewResponses] = useState<
		Record<string, AvailabilityResponse>
	>({});

	// Function to clear all selections
	const clearAllSelections = () => {
		setNewResponses({});
		// If we weren't in edit mode, this would have no visual effect,
		// so we need to force a re-render
		if (!isEditMode) {
			setIsEditMode(true);
			// Then immediately exit edit mode after a small delay
			setTimeout(() => setIsEditMode(false), 50);
		}
	};

	// Check if current user has already submitted
	const userExists =
		props.user_id &&
		props.time_finder_instance.availabilities.some(
			(participant) => participant.id === props.user_id
		);

	// Get existing user responses if available
	const getUserResponses = () => {
		if (props.user_id) {
			const participant = props.time_finder_instance.availabilities.find(
				(p) => p.id === props.user_id
			);

			if (participant) {
				const responses: Record<string, AvailabilityResponse> = {};
				participant.availability.forEach((item) => {
					responses[item.id] = item.status;
				});
				return responses;
			}
		}
		return {};
	};

	// Initialize edit mode with existing responses
	const startEditMode = () => {
		setNewResponses(getUserResponses());
		setIsEditMode(true);
	};

	// Handle response change for a specific date
	const handleResponseChange = (
		dateId: string,
		response: AvailabilityResponse
	) => {
		setNewResponses((prev) => ({
			...prev,
			[dateId]: response,
		}));
	};

	// Cycle through response options
	const cycleResponse = (dateId: string) => {
		const currentResponse = newResponses[dateId];
		let nextResponse: AvailabilityResponse;

		if (!currentResponse) {
			nextResponse = "yes";
		} else if (currentResponse === "yes") {
			nextResponse = "maybe";
		} else if (currentResponse === "maybe") {
			nextResponse = "no";
		} else {
			nextResponse = "yes";
		}

		handleResponseChange(dateId, nextResponse);
	};

	// Submit new participant
	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// If user already exists and is not in edit mode, do nothing
		if (userExists && !isEditMode) {
			return;
		}

		// Call the onSubmit prop with the availabilities data
		props.onSubmit({
			availabilities: props.time_finder_instance.datetimes
				.map((dateOption) => ({
					id: dateOption.id,
					availability: newResponses[dateOption.id],
				}))
				.filter((val) => val.availability !== undefined),
		});
	};

	// Get response icon based on response type
	const getResponseIcon = (response: AvailabilityResponse) => {
		let responseIcon: string | null = null;
		switch (response) {
			case "yes":
				responseIcon = "✓";
				break;
			case "maybe":
				responseIcon = "?";
				break;
			case "no":
				responseIcon = "✗";
				break;
			default:
				responseIcon = null;
		}

		return (
			<div
				className={classNames(
					"flex h-full w-[96px] flex-shrink-0 items-center justify-center border-r border-t border-base-light text-xl text-black",
					{
						"bg-green-500  font-bold ": response === "yes",
						"bg-yellow-500  font-bold ": response === "maybe",
						"bg-red-500  font-bold ": response === "no",
					}
				)}
			>
				<div>{responseIcon}</div>
			</div>
		);
	};

	// Get response button class based on response
	const getResponseButtonClass = (dateId: string) => {
		const response = newResponses[dateId];
		let baseClass =
			"flex flex-col items-center justify-center w-full h-12 rounded-md cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md relative";

		if (!response) {
			return (
				baseClass +
				" border-2 border-dashed border-sesh-dark bg-base-dark hover:bg-base-light/10"
			);
		}

		switch (response) {
			case "yes":
				return (
					baseClass + " bg-green-500 text-black hover:bg-green-400"
				);
			case "maybe":
				return (
					baseClass + " bg-yellow-500 text-black hover:bg-yellow-400"
				);
			case "no":
				return baseClass + " bg-red-500 text-black hover:bg-red-400";
			default:
				return (
					baseClass +
					" border-2 border-dashed border-base-light bg-base-primary hover:bg-base-light/10"
				);
		}
	};

	// Get response display content
	const getResponseContent = (dateId: string) => {
		const response = newResponses[dateId];

		if (!response) {
			return (
				<div className="flex flex-col items-center justify-center p-1">
					<span className="text-sm italic text-sesh-dark">
						<i className="fas fa-plus-circle text-lg text-sesh-dark"></i>
					</span>
				</div>
			);
		}

		switch (response) {
			case "yes":
				return (
					<div className="flex flex-col items-center">
						<div className="text-lg font-bold">✓</div>
						<div className="text-xs">Available</div>
					</div>
				);
			case "maybe":
				return (
					<div className="flex flex-col items-center">
						<div className="text-lg font-bold">?</div>
						<div className="text-xs">Maybe</div>
					</div>
				);
			case "no":
				return (
					<div className="flex flex-col items-center">
						<div className="text-lg font-bold">✗</div>
						<div className="text-xs">Unavailable</div>
					</div>
				);
			default:
				return <div>Select</div>;
		}
	};

	// Handle navigation to create new poll
	const handleCreateNewPoll = () => {
		history.push(props.create_new_poll_link);
	};

	// Format time to display
	const formatTime = (dateTime: DateTime) => {
		return dateTime.toFormat("h:mm a");
	};

	// Calculate and format duration between two DateTime objects
	const formatDuration = (start: DateTime, end: DateTime) => {
		// Create new DateTime objects with only time components
		const startTime = DateTime.fromObject({
			hour: start.hour,
			minute: start.minute,
		});

		let endTime = DateTime.fromObject({
			hour: end.hour,
			minute: end.minute,
		});

		// If endTime is earlier than startTime, assume it's the next day
		if (endTime < startTime) {
			endTime = endTime.plus({ days: 1 });
		}

		const diff = endTime.diff(startTime, ["hours", "minutes"]);
		const hours = Math.floor(diff.hours);
		const minutes = Math.floor(diff.minutes);

		let result = "";
		if (hours > 0) {
			result += `${hours}h `;
		}
		if (minutes > 0 || hours === 0) {
			result += `${minutes}m`;
		}
		return result.trim();
	};

	const handleEditPoll = () => {
		if (props.onEdit) {
			props.onEdit();
		}
	};

	return (
		<div className="availability-poll flex w-full flex-col">
			{/* Public Poll Info Banner - only show for public polls */}
			{props.time_finder_instance.is_public && shareableUrl && (
				<div className="mb-4 mt-2 rounded-md border border-base-light bg-base-dark/30 p-3">
					<div className="flex items-center gap-2 text-white">
						<i className="fas fa-globe text-sesh-primary"></i>
						<span className="font-medium">
							This is a public poll that anyone with the following
							link can access
						</span>
					</div>
					<div className="mt-2 flex items-center gap-2">
						<input
							type="text"
							readOnly
							value={shareableUrl}
							className="flex-1 rounded-md border border-base-light bg-base-dark p-2 text-sm"
							onClick={(e) => e.currentTarget.select()}
						/>
						<div className="relative">
							<button
								className="rounded-md bg-sesh-primary p-2 text-black"
								onClick={handleCopyPublicLink}
								title="Copy to clipboard"
							>
								<i className="fas fa-copy"></i>
							</button>
							{showCopied && (
								<div className="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 rounded bg-sesh-primary px-2 py-1 text-xs font-bold text-black">
									Copied
									<div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-sesh-primary"></div>
								</div>
							)}
						</div>
					</div>
					{!props.time_finder_instance.guild_id && (
						<div className="my-2 mt-3 rounded-md border-sesh-primary/30 bg-base-primary/30 text-sm text-white">
							<a
								href="/dashboard"
								className="flex items-center gap-2 font-medium hover:text-sesh-primary"
							>
								<i className="fas fa-info-circle text-sesh-primary"></i>
								Use{" "}
								<span className="sesh-text relative top-[1.5px] inline-block align-middle">
									sesh
								</span>{" "}
								to create private polls and events for your
								Discord community
							</a>
						</div>
					)}
				</div>
			)}

			<InputSection
				title={
					<div className="flex w-full items-center">
						<div className="flex items-center">
							<i className="fas fa-calendar-check fa-lg mr-2"></i>
							{props.time_finder_instance.title}
						</div>
						<div className="ml-auto flex gap-1">
							{props.is_editable && props.onEdit && (
								<button
									onClick={handleEditPoll}
									className="rounded bg-base-dark px-2 py-1 text-sm text-sesh-primary hover:bg-base-light"
									title="Edit poll"
								>
									<i className="fas fa-edit mr-1"></i>
									Edit
								</button>
							)}
							{props.onDelete && (
								<button
									onClick={props.onDelete}
									disabled={props.is_deleting}
									className="rounded bg-base-dark px-2 py-1 text-sm text-red-400 hover:bg-base-light hover:text-red-300 disabled:opacity-50"
									title="Delete poll"
								>
									{props.is_deleting ? (
										<>
											<i className="fas fa-spinner fa-spin mr-1"></i>
											Deleting...
										</>
									) : (
										<>
											<i className="fas fa-trash mr-1"></i>
											Delete
										</>
									)}
								</button>
							)}
						</div>
					</div>
				}
			>
				{props.time_finder_instance.description && (
					<p className="py-3">
						{props.time_finder_instance.description}
					</p>
				)}

				{/* Poll Settings Indicators */}
				{(props.time_finder_instance.anonymous_voting ||
					props.time_finder_instance.hide_votes ||
					(props.time_finder_instance.eligible_role_ids &&
						props.time_finder_instance.eligible_role_ids.length >
							0)) && (
					<div className="mt-1 flex flex-wrap gap-2">
						{props.time_finder_instance.anonymous_voting && (
							<WithTooltipPortaled
								style="Info"
								tooltip={
									TIME_FINDER_SETTINGS.anonymous_voting
										.description
								}
								no_delay
							>
								<span className="inline-flex items-center rounded-full bg-base-dark px-2 py-1 text-xs font-medium text-sesh-primary">
									<i className="fas fa-user-secret mr-1"></i>
									{
										TIME_FINDER_SETTINGS.anonymous_voting
											.title
									}
								</span>
							</WithTooltipPortaled>
						)}
						{props.time_finder_instance.hide_votes && (
							<WithTooltipPortaled
								style="Info"
								tooltip={
									TIME_FINDER_SETTINGS.hide_votes.description
								}
								no_delay
							>
								<span className="inline-flex items-center rounded-full bg-base-dark px-2 py-1 text-xs font-medium text-sesh-primary">
									<i className="fas fa-eye-slash mr-1"></i>
									{TIME_FINDER_SETTINGS.hide_votes.title}
								</span>
							</WithTooltipPortaled>
						)}
						{props.time_finder_instance.eligible_role_ids &&
							props.time_finder_instance.eligible_role_ids
								.length > 0 && (
								<EligibleRolesIndicator
									eligibleRoleIds={
										props.time_finder_instance
											.eligible_role_ids
									}
								/>
							)}
					</div>
				)}
			</InputSection>

			{/* Modal for just created polls */}
			<DashboardUtils.SeshModal
				is_active={isJustCreatedModalActive}
				title="Poll Created"
				content={
					<JustCreatedModalContent
						shareableUrl={shareableUrl}
						onClose={() => setIsJustCreatedModalActive(false)}
					/>
				}
				onClose={() => {
					setIsJustCreatedModalActive(false);
					const url = new URL(window.location.href);
					url.searchParams.delete("just_created");
					window.history.replaceState(
						{},
						document.title,
						url.toString()
					);
				}}
			/>

			{/* Show message if votes are hidden until voted */}
			{props.time_finder_instance.hide_votes &&
				!props.time_finder_instance.has_user_submitted &&
				props.time_finder_instance.availabilities.length === 0 && (
					<div className="mt-4 rounded-b-md bg-base-semidark px-4 py-8">
						<div className="flex flex-col items-center justify-center gap-2">
							<i className="fas fa-eye-slash text-2xl text-sesh-primary"></i>
							<p className="text-white">
								Other participants' votes are hidden until you
								submit your own response
							</p>
						</div>
					</div>
				)}

			{/* Scrollable container */}
			<div className="mt-4 w-fit max-w-full self-center overflow-hidden rounded-md border border-base-dark">
				<div className="overflow-x-auto pb-2">
					{/* Grid header */}
					<div className="flex min-w-max">
						{/* Fixed name column header */}
						<div className="left-0 z-20 w-[200px] flex-shrink-0 border-r border-base-light bg-base-dark p-3 font-bold text-white lg:sticky">
							<span className="truncate">Name</span>
						</div>

						{/* Date headers */}
						{props.time_finder_instance.datetimes.map(
							(dateOption) => {
								const start_date = props.time_finder_instance
									.are_times_specified
									? DateTime.fromMillis(
											dateOption.start_time
									  ).toLocal()
									: DateTime.fromMillis(
											dateOption.start_time
									  ).toUTC();
								const end_date = dateOption.end_time
									? props.time_finder_instance
											.are_times_specified
										? DateTime.fromMillis(
												dateOption.end_time
										  ).toLocal()
										: DateTime.fromMillis(
												dateOption.end_time
										  ).toUTC()
									: null;

								const tooltipText = props.time_finder_instance
									.are_times_specified
									? `${formatTime(start_date)} - ${formatTime(
											end_date
									  )}`
									: "";

								// Count people who selected 'yes' for this option
								const yesCount =
									props.time_finder_instance.availabilities.filter(
										(participant) =>
											participant.availability.some(
												(a) =>
													a.id === dateOption.id &&
													a.status === "yes"
											)
									).length;

								return (
									<WithTooltipPortaled
										key={dateOption.id}
										style="Info"
										no_delay
										tooltip={
											props.time_finder_instance.guild_id
												? "Create event at this time"
												: null
										}
									>
										<div
											key={dateOption.start_time}
											className="group relative flex w-[96px] flex-shrink-0 cursor-pointer flex-col gap-1 border-r border-base-light bg-base-dark p-1 py-4 text-center text-white hover:bg-base-light"
											onClick={() => {
												if (
													!props.time_finder_instance
														.guild_id
												) {
													return;
												}

												const url =
													redirectToEventCreate({
														event_title:
															props
																.time_finder_instance
																.title,
														event_description:
															props
																.time_finder_instance
																.description,
														guild_id:
															props
																.time_finder_instance
																.guild_id,
														start_time: start_date,
														end_time: end_date,
														is_all_day:
															!props
																.time_finder_instance
																.are_times_specified,
													});

												if (props.is_on_dashboard) {
													history.push(url);
												} else {
													window.open(url);
												}
											}}
										>
											{props.time_finder_instance
												.guild_id && (
												<div className="absolute right-2 top-1 text-xs text-sesh-primary opacity-25 transition-all group-hover:opacity-100">
													<i className="fas fa-plus-circle"></i>
												</div>
											)}
											<div className="whitespace-nowrap text-sm font-bold">
												{start_date.toFormat("EEE")}
											</div>
											<div className="whitespace-nowrap text-sm">
												{start_date.toFormat("MMM d")}
											</div>
											{props.time_finder_instance
												.are_times_specified && (
												<>
													<div className="mt-1 whitespace-nowrap text-xs">
														{formatTime(start_date)}
													</div>
													<div
														className="flex cursor-help items-center justify-center gap-1 text-xs text-gray-300"
														title={tooltipText}
														data-tooltip-instant="true"
													>
														<i className="fas fa-clock text-xs"></i>
														{formatDuration(
															start_date,
															end_date
														)}
													</div>
												</>
											)}
											<div className="mt-1 flex items-center justify-center gap-1 rounded-full bg-base-light px-2 py-0.5 text-xs text-white">
												<i className="fas fa-user-check text-xs"></i>
												{yesCount}
											</div>
										</div>
									</WithTooltipPortaled>
								);
							}
						)}
					</div>

					{/* Only show new participant form row if user hasn't submitted yet or is in edit mode */}
					{(!userExists || (isEditMode && !props.user_id)) &&
						isUserRowVisible && (
							<div className="flex min-w-max bg-base-light/5">
								{/* Name cell with highlight */}
								<div className="left-0 z-10 flex w-[200px] flex-shrink-0 items-center whitespace-nowrap border-l-4 border-r border-t border-base-light border-l-sesh-primary bg-base-primary p-3 font-medium lg:sticky">
									<div className="mr-auto flex items-center">
										<span
											className="max-w-[160px] truncate font-semibold text-sesh-primary"
											title="Your availability"
										>
											Your availability
										</span>
										{isEditMode && (
											<span className="ml-1 text-xs text-sesh-primary">
												(Editing)
											</span>
										)}
									</div>
									<button
										onClick={() =>
											setIsUserRowVisible(false)
										}
										className="ml-1 flex items-center text-xs font-medium text-red-500 hover:text-red-400"
										title="Remove your response"
									>
										<i className="fas fa-times"></i>
									</button>
								</div>

								{/* Response option cells */}
								{props.time_finder_instance.datetimes.map(
									(dateOption) => (
										<div
											key={dateOption.start_time}
											className="w-[96px] flex-shrink-0 border-r border-t border-base-light p-1 text-center"
										>
											<div
												className={getResponseButtonClass(
													dateOption.id
												)}
												onClick={() =>
													cycleResponse(dateOption.id)
												}
												title="Click to cycle through options"
											>
												{getResponseContent(
													dateOption.id
												)}
											</div>
										</div>
									)
								)}
							</div>
						)}

					{/* Participant rows */}
					{props.time_finder_instance.availabilities.map(
						(participant) => {
							// Check if this is the current user's row and they're in edit mode
							const isCurrentUserEditing =
								isEditMode &&
								props.user_id &&
								props.user_id === participant.id;

							return (
								<div
									key={participant.id}
									className={classNames("flex w-max", {
										"hover:bg-base-light/10":
											!isCurrentUserEditing,
										"bg-base-light/20":
											isCurrentUserEditing,
									})}
								>
									{/* Fixed name column */}
									<div className="left-0 z-10 flex w-[200px] flex-shrink-0 items-center justify-between whitespace-nowrap border-r border-t border-base-light bg-base-primary p-3 font-medium lg:sticky">
										{props.UserRenderer &&
										!props.time_finder_instance
											.anonymous_voting &&
										participant.type === "discord" ? (
											<props.UserRenderer
												user_id={participant.id}
											/>
										) : (
											<div className="flex min-w-0 items-center gap-1">
												{participant.avatar_url ? (
													<img
														src={`https://cdn.discordapp.com/${participant.avatar_url}`}
														alt={
															participant.username
														}
														className="mr-2 h-6 w-6 flex-shrink-0 rounded-full"
													/>
												) : (
													<RandomAvatar
														seed={participant.id}
														size={24}
														className="mr-2 flex-shrink-0"
													/>
												)}
												<span
													className="truncate"
													title={
														participant.username ??
														participant.id
													}
												>
													{participant.username ??
														participant.id}
												</span>
											</div>
										)}

										{/* Show edit button if this is the current user's row */}
										{props.user_id &&
											props.user_id === participant.id &&
											!isEditMode && (
												<button
													onClick={startEditMode}
													className="ml-2 flex items-center text-xs font-medium text-sesh-primary hover:underline"
												>
													<i className="fas fa-edit mr-1"></i>
												</button>
											)}
									</div>

									{/* Response cells */}
									{props.time_finder_instance.datetimes.map(
										(dateOption) => {
											// If user is editing this row, show the editable cells
											if (isCurrentUserEditing) {
												return (
													<div
														key={dateOption.id}
														className="w-[96px] flex-shrink-0 border-r border-t border-base-light p-1 text-center"
													>
														<div
															className={getResponseButtonClass(
																dateOption.id
															)}
															onClick={() =>
																cycleResponse(
																	dateOption.id
																)
															}
															title="Click to cycle through options"
														>
															{getResponseContent(
																dateOption.id
															)}
														</div>
													</div>
												);
											}

											// Otherwise show the static response
											return (
												<div
													key={dateOption.id}
													className="w-[96px] flex-shrink-0 border-r border-t border-base-light"
												>
													{getResponseIcon(
														participant.availability.find(
															(val) =>
																val.id ===
																dateOption.id
														)?.status
													)}
												</div>
											);
										}
									)}
								</div>
							);
						}
					)}
				</div>
			</div>

			<div className="mt-4 flex flex-col items-end">
				{/* Show submit buttons or option to add response */}
				{(!userExists || isEditMode) && (
					<>
						{isUserRowVisible ? (
							<div className="flex w-fit gap-2">
								{isEditMode && (
									<div className="w-fit">
										<SeshPrimaryButton
											onClick={() => setIsEditMode(false)}
											className=" bg-gray-500 hover:bg-gray-600"
										>
											Cancel
										</SeshPrimaryButton>
									</div>
								)}
								<div className="w-fit">
									<SeshPrimaryButton
										onClick={handleSubmit}
										is_loading={props.is_loading}
									>
										{isEditMode
											? "Update Response"
											: "Submit Response"}
									</SeshPrimaryButton>
								</div>
							</div>
						) : (
							<div className="w-fit">
								<SeshPrimaryButton
									onClick={() => setIsUserRowVisible(true)}
								>
									Add Your Response
								</SeshPrimaryButton>
							</div>
						)}
					</>
				)}
				<div className="mt-3 text-right">
					<span
						onClick={handleCreateNewPoll}
						className="cursor-pointer text-sm font-bold text-sesh-primary hover:underline"
					>
						Create New Time Finder Poll
					</span>
				</div>
			</div>
		</div>
	);
}

function redirectToEventCreate(opts: {
	event_title: string;
	event_description: string;
	guild_id: string;
	start_time: DateTime;
	end_time: DateTime;
	is_all_day: boolean;
}) {
	const start_time = opts.is_all_day
		? opts.start_time.set({
				hour: 12,
				minute: 0,
				second: 0,
				millisecond: 0,
		  })
		: opts.start_time;

	const local_time = DateTime.local().set({
		hour: start_time.hour,
		minute: start_time.minute,
		second: start_time.second,
		millisecond: start_time.millisecond,
		day: start_time.day,
		month: start_time.month,
		year: start_time.year,
	});

	const start_time_str = local_time.toISO().replaceAll("+", "%2b");

	return common_utils.getEventCreateURL(opts.guild_id, undefined, undefined, {
		date: encodeURIComponent(start_time_str),
		title: encodeURIComponent(opts.event_title),
		description: encodeURIComponent(opts.event_description),
	});
}
